import { db } from '../config/firebase';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  getDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  limit
} from 'firebase/firestore';

/**
 * Cria um convite para um usuário se tornar profissional de uma barbearia
 * @param {string} barbershopId - ID da barbearia
 * @param {string} barbershopName - Nome da barbearia
 * @param {string} invitedUserId - ID do usuário convidado
 * @param {string} invitedBy - ID do usuário que está enviando o convite
 * @param {string} invitedByName - Nome do usuário que está enviando o convite
 * @returns {Promise<string>} ID do convite criado
 */
export const createInvitation = async (barbershopId, barbershopName, invitedUserId, invitedBy, invitedByName) => {
  try {
    // Verificar se já existe um convite pendente
    const existingQuery = query(
      collection(db, 'barbershopInvitations'),
      where('barbershopId', '==', barbershopId),
      where('invitedUserId', '==', invitedUserId),
      where('status', '==', 'pending')
    );
    
    const existingSnapshot = await getDocs(existingQuery);
    if (!existingSnapshot.empty) {
      throw new Error('Já existe um convite pendente para este usuário nesta barbearia');
    }

    // Verificar se o usuário já é dono da barbearia e obter dados do usuário
    const userDoc = await getDoc(doc(db, 'users', invitedUserId));
    if (!userDoc.exists()) {
      throw new Error('Usuário não encontrado');
    }
    
    const userData = userDoc.data();
    
    // Verificar se já é dono da barbearia
    if (userData.barbershops && userData.barbershops.includes(barbershopId)) {
      throw new Error('Este usuário já é profissional desta barbearia');
    }
    if (userData.barbershopId === barbershopId) {
      throw new Error('Este usuário já é profissional desta barbearia');
    }

    // Criar o convite com dados do usuário convidado
    const invitationData = {
      barbershopId,
      barbershopName,
      invitedUserId,
      invitedUserName: userData.name || 'Usuário',
      invitedUserEmail: userData.email || '',
      invitedBy,
      invitedByName,
      status: 'pending',
      createdAt: serverTimestamp(),
      expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)) // 7 dias
    };

    const docRef = await addDoc(collection(db, 'barbershopInvitations'), invitationData);
    
    // Criar notificação para o usuário convidado
    await addDoc(collection(db, 'notifications'), {
      userId: invitedUserId,
      type: 'barbershop_invitation',
      title: 'Convite para ser profissional',
      message: `${invitedByName} convidou você para ser profissional em ${barbershopName}`,
      data: {
        invitationId: docRef.id,
        barbershopId,
        barbershopName
      },
      read: false,
      createdAt: serverTimestamp()
    });

    return docRef.id;
  } catch (error) {
    console.error('Erro ao criar convite:', error);
    throw error;
  }
};

/**
 * Aceita um convite de barbearia
 * @param {string} invitationId - ID do convite
 * @param {string} userId - ID do usuário aceitando o convite
 * @returns {Promise<void>}
 */
export const acceptInvitation = async (invitationId, userId) => {
  try {
    const invitationRef = doc(db, 'barbershopInvitations', invitationId);
    const invitationDoc = await getDoc(invitationRef);

    if (!invitationDoc.exists()) {
      throw new Error('Convite não encontrado');
    }

    const invitation = invitationDoc.data();

    if (invitation.invitedUserId !== userId) {
      throw new Error('Este convite não é para você');
    }

    if (invitation.status !== 'pending') {
      throw new Error('Este convite não está mais disponível');
    }

    // Verificar se o convite expirou
    if (invitation.expiresAt && invitation.expiresAt.toDate() < new Date()) {
      throw new Error('Este convite expirou');
    }

    // Atualizar o status do convite
    await updateDoc(invitationRef, {
      status: 'accepted',
      acceptedAt: serverTimestamp()
    });

    // Atualizar o usuário para adicionar a barbearia e torná-lo owner
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const barbershops = userData.barbershops || [];
      
      if (!barbershops.includes(invitation.barbershopId)) {
        barbershops.push(invitation.barbershopId);
      }

      await updateDoc(userRef, {
        barbershops,
        role: 'owner'
      });
    }

    // Notificar o dono da barbearia que enviou o convite
    await addDoc(collection(db, 'notifications'), {
      userId: invitation.invitedBy,
      type: 'invitation_accepted',
      title: 'Convite aceito',
      message: `Seu convite para ${invitation.barbershopName} foi aceito`,
      data: {
        barbershopId: invitation.barbershopId,
        acceptedBy: userId
      },
      read: false,
      createdAt: serverTimestamp()
    });

  } catch (error) {
    console.error('Erro ao aceitar convite:', error);
    throw error;
  }
};

/**
 * Rejeita um convite de barbearia
 * @param {string} invitationId - ID do convite
 * @param {string} userId - ID do usuário rejeitando o convite
 * @returns {Promise<void>}
 */
export const rejectInvitation = async (invitationId, userId) => {
  try {
    const invitationRef = doc(db, 'barbershopInvitations', invitationId);
    const invitationDoc = await getDoc(invitationRef);

    if (!invitationDoc.exists()) {
      throw new Error('Convite não encontrado');
    }

    const invitation = invitationDoc.data();

    if (invitation.invitedUserId !== userId) {
      throw new Error('Este convite não é para você');
    }

    if (invitation.status !== 'pending') {
      throw new Error('Este convite não está mais disponível');
    }

    // Atualizar o status do convite
    await updateDoc(invitationRef, {
      status: 'rejected',
      rejectedAt: serverTimestamp()
    });

    // Notificar o dono da barbearia que enviou o convite
    await addDoc(collection(db, 'notifications'), {
      userId: invitation.invitedBy,
      type: 'invitation_rejected',
      title: 'Convite recusado',
      message: `Seu convite para ${invitation.barbershopName} foi recusado`,
      data: {
        barbershopId: invitation.barbershopId,
        rejectedBy: userId
      },
      read: false,
      createdAt: serverTimestamp()
    });

  } catch (error) {
    console.error('Erro ao rejeitar convite:', error);
    throw error;
  }
};

/**
 * Busca todos os convites pendentes de um usuário
 * @param {string} userId - ID do usuário
 * @returns {Promise<Array>} Lista de convites pendentes
 */
export const getUserPendingInvitations = async (userId) => {
  try {
    const q = query(
      collection(db, 'barbershopInvitations'),
      where('invitedUserId', '==', userId),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(q);
    const invitations = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      // Verificar se não expirou
      if (!data.expiresAt || data.expiresAt.toDate() > new Date()) {
        invitations.push({
          id: doc.id,
          ...data
        });
      }
    });

    return invitations;
  } catch (error) {
    console.error('Erro ao buscar convites do usuário:', error);
    throw error;
  }
};

/**
 * Busca todos os convites enviados por uma barbearia
 * @param {string} barbershopId - ID da barbearia
 * @returns {Promise<Array>} Lista de convites da barbearia
 */
export const getBarbershopInvitations = async (barbershopId) => {
  try {
    const q = query(
      collection(db, 'barbershopInvitations'),
      where('barbershopId', '==', barbershopId)
    );

    const snapshot = await getDocs(q);
    const invitations = [];

    snapshot.forEach((doc) => {
      invitations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return invitations;
  } catch (error) {
    console.error('Erro ao buscar convites da barbearia:', error);
    throw error;
  }
};

/**
 * Cancela um convite pendente (apenas o criador pode cancelar)
 * @param {string} invitationId - ID do convite
 * @param {string} userId - ID do usuário tentando cancelar
 * @returns {Promise<void>}
 */
export const cancelInvitation = async (invitationId, userId) => {
  try {
    const invitationRef = doc(db, 'barbershopInvitations', invitationId);
    const invitationDoc = await getDoc(invitationRef);

    if (!invitationDoc.exists()) {
      throw new Error('Convite não encontrado');
    }

    const invitation = invitationDoc.data();

    if (invitation.invitedBy !== userId) {
      throw new Error('Você não tem permissão para cancelar este convite');
    }

    if (invitation.status !== 'pending') {
      throw new Error('Apenas convites pendentes podem ser cancelados');
    }

    // Atualizar o status do convite
    await updateDoc(invitationRef, {
      status: 'cancelled',
      cancelledAt: serverTimestamp()
    });

  } catch (error) {
    console.error('Erro ao cancelar convite:', error);
    throw error;
  }
};

/**
 * Remove convites expirados
 * @returns {Promise<number>} Número de convites removidos
 */
export const cleanupExpiredInvitations = async () => {
  try {
    const q = query(
      collection(db, 'barbershopInvitations'),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(q);
    let deletedCount = 0;
    const now = new Date();

    const deletePromises = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.expiresAt && data.expiresAt.toDate() < now) {
        deletePromises.push(deleteDoc(doc.ref));
        deletedCount++;
      }
    });

    await Promise.all(deletePromises);
    return deletedCount;
  } catch (error) {
    console.error('Erro ao limpar convites expirados:', error);
    throw error;
  }
};

/**
 * Busca profissionais de uma barbearia
 * @param {string} barbershopId - ID da barbearia
 * @returns {Promise<Array>} Lista de profissionais com seus dados
 */
export const getBarbershopProfessionals = async (barbershopId) => {
  try {
    // Buscar usuários com formato novo (barbershops array)
    const q1 = query(
      collection(db, 'users'),
      where('barbershops', 'array-contains', barbershopId),
      where('role', '==', 'owner'),
      limit(50)
    );

    // Buscar usuários com formato antigo (barbershopId string)
    const q2 = query(
      collection(db, 'users'),
      where('barbershopId', '==', barbershopId),
      where('role', '==', 'owner'),
      limit(50)
    );

    const [snapshot1, snapshot2] = await Promise.all([
      getDocs(q1),
      getDocs(q2)
    ]);
    
    const professionals = [];
    const addedIds = new Set(); // Para evitar duplicatas

    // Processar resultados do formato novo
    snapshot1.forEach((doc) => {
      if (!addedIds.has(doc.id)) {
        const data = doc.data();
        professionals.push({
          id: doc.id,
          name: data.name,
          email: data.email,
          specialty: data.specialty || 'Não especificada',
          experience: data.experience || 'Não informada',
          photoURL: data.photoURL || null
        });
        addedIds.add(doc.id);
      }
    });

    // Processar resultados do formato antigo
    snapshot2.forEach((doc) => {
      if (!addedIds.has(doc.id)) {
        const data = doc.data();
        professionals.push({
          id: doc.id,
          name: data.name,
          email: data.email,
          specialty: data.specialty || 'Não especificada',
          experience: data.experience || 'Não informada',
          photoURL: data.photoURL || null
        });
        addedIds.add(doc.id);
      }
    });

    return professionals;
  } catch (error) {
    console.error('Erro ao buscar profissionais da barbearia:', error);
    throw error;
  }
};

/**
 * Remove um profissional de uma barbearia
 * @param {string} barbershopId - ID da barbearia
 * @param {string} userId - ID do usuário a ser removido
 * @param {string} removedBy - ID do usuário que está removendo
 * @returns {Promise<void>}
 */
export const removeProfessional = async (barbershopId, userId, removedBy) => {
  try {
    if (userId === removedBy) {
      throw new Error('Você não pode remover a si mesmo');
    }

    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('Usuário não encontrado');
    }

    const userData = userDoc.data();
    const barbershops = userData.barbershops || [];
    const updatedBarbershops = barbershops.filter(id => id !== barbershopId);

    await updateDoc(userRef, {
      barbershops: updatedBarbershops,
      // Se não tiver mais barbearias, volta para role 'user'
      role: updatedBarbershops.length === 0 ? 'user' : 'owner'
    });

    // Notificar o usuário removido
    await addDoc(collection(db, 'notifications'), {
      userId: userId,
      type: 'removed_from_barbershop',
      title: 'Removido da barbearia',
      message: `Você foi removido como profissional de uma barbearia`,
      data: {
        barbershopId
      },
      read: false,
      createdAt: serverTimestamp()
    });

  } catch (error) {
    console.error('Erro ao remover profissional:', error);
    throw error;
  }
};
